import { ProtectionPlan } from '@/models/registry';
import type { ProtectionPlan as ProtectionPlanType } from '@/models/types';
import { makeAutoObservable } from 'mobx';
import { db } from '../db';
import type { DeltaPacketRecord } from '../hydrate';
import { hydrateFromWorker } from '../hydrate-worker-utils';
import type { BaseStore } from './base';

class ProtectionPlanStore
  implements BaseStore<ProtectionPlan, ProtectionPlanType>
{
  store: Map<string, ProtectionPlan> = new Map();

  storeIndexedByForeignKey: Map<string, ProtectionPlan> = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  private setProtectionPlanField = <K extends keyof ProtectionPlanType>(
    protectionPlan: ProtectionPlan,
    key: K,
    value: ProtectionPlanType[K],
  ) => {
    (protectionPlan as Pick<ProtectionPlan, K>)[key] =
      value as ProtectionPlan[K];
  };

  applyDeltaV2({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<ProtectionPlanType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete' || delta.action === 'archive') {
      if (hydrateIndexedDB) {
        db.deal_protection_plans.delete(delta.record_id);
      }
      this.remove({ id: delta.record_id, dealId: delta.data.deal_id });
      return;
    }

    if (delta.action === 'create') {
      if (hydrateIndexedDB) {
        db.deal_protection_plans.put(delta.data, delta.record_id);
      }
      this.upsert(delta.data);
      return;
    }

    if (delta.action === 'update') {
      if (hydrateIndexedDB) {
        db.deal_protection_plans.update(delta.record_id, delta.data);
      }
      this.replaceOnlyUpdatedFields({ delta });
      return;
    }

    if (hydrateIndexedDB) {
      db.deal_protection_plans.put(delta.data, delta.record_id);
    }
    this.upsert(delta.data);
  }

  replaceOnlyUpdatedFields({
    delta,
  }: {
    delta: DeltaPacketRecord<ProtectionPlanType>;
  }) {
    const existingProtectionPlan = this.getById(delta.record_id);
    if (!existingProtectionPlan) return;

    let changed = false;

    const entries = Object.entries(delta.data) as [
      keyof ProtectionPlanType,
      ProtectionPlanType[keyof ProtectionPlanType],
    ][];

    for (const [key, newValue] of entries) {
      if (newValue === undefined || existingProtectionPlan[key] === newValue) {
        continue;
      }

      this.setProtectionPlanField(existingProtectionPlan, key, newValue);
      changed = true;
    }

    if (!changed) return;
    this.save(existingProtectionPlan);
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<ProtectionPlanType>[];
    hydrateIndexedDB?: boolean;
  }): void {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<ProtectionPlanType>;
    hydrateIndexedDB?: boolean;
  }): void {
    if (hydrateIndexedDB) {
      db.deal_protection_plans.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.remove({ id: delta.record_id, dealId: delta.data.deal_id });
      return;
    }

    if (delta.action === 'archive') {
      this.remove({ id: delta.record_id, dealId: delta.data.deal_id });
      return;
    }

    this.upsert(delta.data);
  }

  getById(id: string): ProtectionPlan | undefined {
    return this.store.get(id);
  }

  add(input: ProtectionPlanType) {
    const protectionPlan = new ProtectionPlan(input);
    this.store.set(protectionPlan.id, protectionPlan);
    // Index by foreign key for O(1) lookups
    this.storeIndexedByForeignKey.set(protectionPlan.deal_id, protectionPlan);
  }

  save(protectionPlan: ProtectionPlan) {
    this.store.set(protectionPlan.id, protectionPlan);
    if (protectionPlan.deal_id) {
      this.storeIndexedByForeignKey.set(protectionPlan.deal_id, protectionPlan);
    }
  }

  // actions
  upsert(protectionPlan: ProtectionPlanType) {
    const existingProtectionPlan = this.getById(protectionPlan.id);
    if (existingProtectionPlan) {
      this.replace({ id: protectionPlan.id, protectionPlan });
    } else {
      this.add(protectionPlan);
    }
  }

  replace({
    id,
    protectionPlan,
  }: {
    id: string;
    protectionPlan: ProtectionPlanType;
  }) {
    const existingProtectionPlan = this.getById(id);
    if (!existingProtectionPlan) return;

    // update the protection plan
    existingProtectionPlan.key = protectionPlan.key;
    existingProtectionPlan.name = protectionPlan.name;
    existingProtectionPlan.type = protectionPlan.type;
    existingProtectionPlan.term_length = protectionPlan.term_length;
    existingProtectionPlan.term_miles = protectionPlan.term_miles;
    existingProtectionPlan.monthly_price = protectionPlan.monthly_price;
    existingProtectionPlan.price = protectionPlan.price;

    this.save(existingProtectionPlan);
  }

  remove({ id, dealId }: { id: string; dealId: string | null | undefined }) {
    this.store.delete(id);
    db.deal_protection_plans.delete(id);
    if (!dealId) return;
    this.storeIndexedByForeignKey.delete(dealId);
  }

  hydrateLocal(): Promise<void> {
    return hydrateFromWorker({
      tableName: 'deal_protection_plans',
      entity: 'deal_protection_plans',
      modelStore: protectionPlanStore,
    });
  }

  async hydrateDeal(dealId: ProtectionPlanType['deal_id']): Promise<void> {
    const items = await db.deal_protection_plans.toArray();
    items.forEach((item) => {
      if (item.deal_id === dealId) this.upsert(item);
    });
  }
}

export const protectionPlanStore = new ProtectionPlanStore();
